'use client';

import {useCourseEnrollmentByIdQuery} from '@/generated/graphql';
import {useIsLoggedIn} from '@/lib/hooks/use-is-logged-in';

/**
 * Hook to check if a user is enrolled in a specific course
 * @param courseId - The ID of the course to check enrollment for
 * @returns boolean or undefined - false if user is logged out or on error (graceful failure), undefined if the course enrollment is not loaded (to trigger loading state)
 */
export function useEnrolledInCourse(courseId: string) {
    const isLoggedIn = useIsLoggedIn();

    const courseEnrollment = useCourseEnrollmentByIdQuery(
        {id: courseId},
        {
            enabled: isLoggedIn,
        },
    );

    // If user is not logged in, they're not enrolled
    if (!isLoggedIn) {
        return false;
    }

    // On error, fail gracefully and assume user is not enrolled
    // This prevents infinite loading states when the enrollment query fails
    if (courseEnrollment.isError) {
        return false;
    }

    // If the query succeeded but returned no data or no course, fail gracefully
    // This handles GraphQL errors that return HTTP 200 but with errors in the response
    if (courseEnrollment.isSuccess && !courseEnrollment.data?.course) {
        return false;
    }

    return courseEnrollment.data?.course?.isEnrolled;
}
